import { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, Cell } from "recharts";
import { Zap, Sun, Battery, Leaf, TreePine, Gauge, AlertTriangle, Ticket, ShieldCheck } from "lucide-react";
import UptimeRing from "@/components/UptimeRing";
import TechnicalGuide from "@/components/TechnicalGuide";
import { StatusBadge } from "@/components/StatusBadge";

const REFRESH_MS = 60000;

function formatEnergy(kwh) {
    if (kwh == null) return { value: "0", unit: "kWh" };
    if (kwh < 1000) return { value: kwh.toLocaleString(undefined, { maximumFractionDigits: 1 }), unit: "kWh" };
    return { value: (kwh / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 }), unit: "MWh" };
}

/**
 * My Solar — Customer landing page.
 * Uses /api/customer/summary (RBAC-scoped) then renders each plant's KPIs,
 * uptime, 30-day chart, active alerts, tickets & documents.
 */
export default function MySolar() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [hover, setHover] = useState(null);

    const load = useCallback(() => api.get("/customer/summary")
        .then(r => {
            setData(r.data);
            setSelectedId(prev => prev || r.data.plants?.[0]?.id || null);
        })
        .catch(err => toast.error(formatErr(err.response?.data?.detail) || "Failed to load your solar data"))
        .finally(() => setLoading(false)), []);

    useEffect(() => {
        load();
        const t = setInterval(load, REFRESH_MS);
        return () => clearInterval(t);
    }, [load]);

    if (loading) return <div className="text-zinc-500 text-sm" data-testid="cust-loading">Loading your solar telemetry…</div>;
    if (!data || data.plants.length === 0) {
        return (
            <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center" data-testid="cust-no-plants">
                <Sun className="w-8 h-8 mx-auto text-zinc-300 mb-3" />
                <div className="text-sm text-zinc-600 font-medium">No plants linked to your account yet.</div>
                <div className="text-xs text-zinc-500 mt-1">Contact RPPL Solar to link your solar system.</div>
            </div>
        );
    }

    const { totals, plants } = data;
    const selected = plants.find(p => p.id === selectedId) || plants[0];
    const todayFmt = formatEnergy(totals.today_kwh);
    const lifeFmt = formatEnergy(totals.lifetime_kwh);

    return (
        <div className="space-y-6" data-testid="my-solar-page">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">My Solar</div>
                    <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 mt-1">
                        Welcome — your {plants.length > 1 ? `${plants.length} systems` : "system"} at a glance
                    </h1>
                </div>
                <div className="text-xs text-zinc-500 font-mono flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#16A34A] animate-pulse" />
                    LIVE · refreshes every 60s
                </div>
            </div>

            {/* Sustainability Hero */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="cust-hero">
                <HeroCard icon={Gauge} label="Total Capacity" value={(totals.installed_capacity_kw ?? 0).toLocaleString()} unit="kWp" sub={`across ${plants.length} plant${plants.length !== 1 ? "s" : ""}`} testid="cust-hero-capacity" />
                <HeroCard icon={Leaf} label="CO₂ Saved" value={((totals.co2_saved_kg ?? 0) / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} unit="tonnes" sub={`from ${lifeFmt.value} ${lifeFmt.unit} clean energy`} testid="cust-hero-co2" />
                <HeroCard icon={TreePine} label="Trees Planted" value={(totals.trees_equivalent ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} unit="trees" sub="equivalent CO₂ absorbed annually" testid="cust-hero-trees" />
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="cust-kpi-grid">
                <KpiTile icon={Sun} label="Today's Gen" value={todayFmt.value} unit={todayFmt.unit} accent="green" testid="cust-kpi-today" />
                <KpiTile icon={Battery} label="Lifetime" value={lifeFmt.value} unit={lifeFmt.unit} accent="green" testid="cust-kpi-lifetime" />
                <KpiTile icon={AlertTriangle} label="Active Alerts" value={totals.active_alerts ?? 0} accent={totals.active_alerts > 0 ? "orange" : "black"} testid="cust-kpi-alerts" />
                <KpiTile icon={Ticket} label="Open Tickets" value={totals.open_tickets ?? 0} accent={totals.open_tickets > 0 ? "orange" : "black"} testid="cust-kpi-tickets" />
            </div>

            {/* Uptime + Plant switcher */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm p-5" data-testid="cust-uptime-panel">
                    <div className="flex items-center gap-2 mb-3">
                        <ShieldCheck className="w-4 h-4 text-[#16A34A]" />
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">System Availability</div>
                            <div className="text-sm text-zinc-700 mt-0.5">Average uptime across your fleet · Solar hours 06:00–18:00 IST</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-6 flex-wrap">
                        <UptimeRing
                            uptime={{ uptime_pct: totals.avg_uptime_pct, in_daylight: totals.avg_uptime_pct != null }}
                            label="Average Uptime"
                            testid="cust-avg-uptime"
                            size={92}
                        />
                        {plants.length > 1 && (
                            <div className="text-[11px] text-zinc-500 flex-1 min-w-[220px]">
                                Switch plants below to see the individual chart and paperwork.
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="cust-plant-switcher">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-2">Your Plants</div>
                    <div className="space-y-1.5 max-h-[220px] overflow-y-auto">
                        {plants.map((p, i) => {
                            const active = p.id === selected.id;
                            return (
                                <button
                                    key={p.id}
                                    onClick={() => setSelectedId(p.id)}
                                    className={`w-full text-left px-3 py-2 rounded-sm border transition-colors ${active ? "bg-[#0B0D11] text-white border-[#0B0D11]" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"}`}
                                    data-testid={`cust-plant-switch-${i}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium truncate">
                                                {p.customer_name || p.name}
                                            </div>
                                            <div className={`text-[10px] font-mono truncate ${active ? "text-zinc-300" : "text-zinc-500"}`}>
                                                {p.location || p.address || "—"} · {p.installed_capacity_kw} kWp
                                            </div>
                                        </div>
                                        <div className={`text-[10px] font-mono flex-shrink-0 ${active ? "text-[#0A0A0A]" : "text-zinc-500"}`}>
                                            {(p.current_power_kw ?? 0).toFixed(1)} kW
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Selected Plant Detail */}
            <PlantSection plant={selected} hover={hover} setHover={setHover} />
        </div>
    );
}

function PlantSection({ plant, hover, setHover }) {
    // Universal Custom Range picker — same functionality customers get
    // that admins/engineers have on the Fleet Plant Detail chart.
    const _todayIst = () => {
        const now = new Date();
        const istMs = now.getTime() + (5.5 * 60 * 60 * 1000) + (now.getTimezoneOffset() * 60 * 1000);
        return new Date(istMs).toISOString().slice(0, 10);
    };
    const _daysAgo = (n) => {
        const now = new Date();
        const istMs = now.getTime() + (5.5 * 60 * 60 * 1000) + (now.getTimezoneOffset() * 60 * 1000);
        const d = new Date(istMs); d.setUTCDate(d.getUTCDate() - n);
        return d.toISOString().slice(0, 10);
    };
    const [tab, setTab] = useState("month");
    const [rangeStart, setRangeStart] = useState(_daysAgo(13));
    const [rangeEnd, setRangeEnd] = useState(_todayIst());
    const [rangeSeries, setRangeSeries] = useState(null);
    const [rangeLoading, setRangeLoading] = useState(false);

    useEffect(() => {
        if (tab !== "custom") return;
        if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) return;
        let cancelled = false;
        setRangeLoading(true);
        api.get(`/plants/${plant.id}/daily-history?start_date=${rangeStart}&end_date=${rangeEnd}`)
            .then(r => { if (!cancelled) setRangeSeries(r.data); })
            .catch(() => { if (!cancelled) setRangeSeries(null); })
            .finally(() => { if (!cancelled) setRangeLoading(false); });
        return () => { cancelled = true; };
    }, [tab, plant.id, rangeStart, rangeEnd]);

    const powerData = (plant.power_history || []).map(d => {
        const ts = typeof d.timestamp === "number" ? d.timestamp * 1000 : d.timestamp;
        return {
            time: new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }),
            kW: d.power_kw || 0,
        };
    });
    const dailyData = (plant.daily_history || []).map(d => ({
        date: d.date.slice(5),
        kWh: d.generation_kwh,
        isToday: !!d.is_today,
    }));
    const _todayForRange = _todayIst();
    const rangeData = (rangeSeries?.daily || []).map(d => ({
        date: d.date.slice(5),
        kWh: d.generation_kwh,
        isToday: d.date === _todayForRange,
    }));
    const rangeTotal = rangeSeries?.total_kwh ?? 0;
    const barData = tab === "custom" ? rangeData : dailyData;

    const desiredHours = new Set(["00", "03", "06", "09", "12", "15", "18", "21"]);
    const powerTicks = [...new Set(
        powerData.filter(d => desiredHours.has(d.time.slice(0, 2)) && d.time.slice(3, 5) === "00").map(d => d.time)
    )];

    const activeAlerts = plant.active_alerts || [];

    return (
        <div className="space-y-6" data-testid="cust-plant-section">
            {/* Plant header */}
            <div className="bg-white border border-zinc-200 rounded-sm p-5">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                    <div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <h2 className="text-xl font-semibold tracking-tight text-zinc-900">{plant.customer_name || plant.name}</h2>
                            <StatusBadge health={plant.health} />
                            {typeof plant.warranty_days_remaining === "number" && (() => {
                                const d = plant.warranty_days_remaining;
                                const active = plant.warranty_active;
                                const tone = !active ? { c: "#B91C1C", bg: "#FEE2E2", b: "#FCA5A5" } : d < 60 ? { c: "#B91C1C", bg: "#FEE2E2", b: "#FCA5A5" } : d < 365 ? { c: "#B45309", bg: "#FEF3C7", b: "#FCD34D" } : { c: "#166534", bg: "#DCFCE7", b: "#86EFAC" };
                                const yrs = Math.floor(Math.abs(d) / 365);
                                const mo = Math.floor((Math.abs(d) % 365) / 30);
                                return (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded-full" style={{ background: tone.bg, color: tone.c, borderWidth: 1, borderStyle: "solid", borderColor: tone.b }} data-testid="cust-warranty-badge">
                                        {active ? "🛡" : "⚠"} {active ? `${yrs}y ${mo}m warranty` : `Expired ${yrs}y ${mo}m ago`}
                                    </span>
                                );
                            })()}
                        </div>
                        <div className="text-sm text-zinc-500 mt-1">{plant.location} · {plant.address}</div>
                        {(plant.customer_email || plant.customer_phone) && (
                            <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3 flex-wrap" data-testid="cust-contact-row">
                                {plant.customer_email && (
                                    <span className="inline-flex items-center gap-1"><span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Email</span> <span className="font-mono text-zinc-700">{plant.customer_email}</span></span>
                                )}
                                {plant.customer_phone && (
                                    <span className="inline-flex items-center gap-1"><span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Mobile</span> <span className="font-mono text-zinc-700">{plant.customer_phone}</span></span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MiniKpi label="Capacity" value={`${plant.installed_capacity_kw}`} unit="kWp" />
                    <MiniKpi label="Live Power" value={plant.current_power_kw?.toFixed(1)} unit="kW" accent="orange" />
                    <MiniKpi label="Today" value={plant.today_realtime_kwh?.toFixed(1) ?? "0.0"} unit="kWh" accent="green" />
                    <MiniKpi label="Uptime" value={plant.uptime?.uptime_pct != null ? `${plant.uptime.uptime_pct.toFixed(1)}%` : "—"} accent="green" />
                </div>
            </div>

            {/* Per-plant uptime ring */}
            <UptimeRing uptime={plant.uptime} testid="cust-plant-uptime" />

            {/* Production chart — 30-day summary or a custom date range. */}
            <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid="cust-chart">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Production</div>
                        <div className="text-sm text-zinc-700 mt-0.5 inline-flex items-center gap-2 flex-wrap">
                            {tab === "custom"
                                ? (rangeStart && rangeEnd && rangeStart <= rangeEnd
                                    ? `${rangeStart} → ${rangeEnd} · Total ${rangeTotal.toFixed(1)} kWh`
                                    : "Select a valid start and end date")
                                : (
                                    <>
                                        Last 30 days · daily
                                        <span className="inline-flex items-center gap-1 text-[10px] font-mono text-[#16A34A] px-1.5 py-0.5 rounded-sm bg-green-50 border border-green-200">
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A] animate-pulse" />
                                            LIVE · Today {plant.today_realtime_kwh?.toFixed(1) ?? "0.0"} kWh
                                        </span>
                                    </>
                                )}
                        </div>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap">
                        {tab === "custom" && (
                            <div className="flex items-center gap-2 flex-wrap" data-testid="cust-range-picker">
                                <input
                                    type="date"
                                    value={rangeStart}
                                    max={rangeEnd || _todayIst()}
                                    onChange={e => setRangeStart(e.target.value)}
                                    className="h-8 px-2 w-[150px] rounded-sm text-xs font-mono border border-zinc-200"
                                    data-testid="cust-range-start"
                                />
                                <span className="text-xs text-zinc-500">→</span>
                                <input
                                    type="date"
                                    value={rangeEnd}
                                    min={rangeStart}
                                    max={_todayIst()}
                                    onChange={e => setRangeEnd(e.target.value)}
                                    className="h-8 px-2 w-[150px] rounded-sm text-xs font-mono border border-zinc-200"
                                    data-testid="cust-range-end"
                                />
                            </div>
                        )}
                        <div className="text-right">
                            <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
                                {hover ? hover.date : tab === "custom" ? "Range peak" : "30-day peak"}
                            </div>
                            <div className="text-2xl font-mono font-medium text-[#0A0A0A] leading-tight">
                                {(hover ? hover.kWh : Math.max(0, ...barData.map(d => d.kWh), 0)).toFixed(1)}
                                <span className="text-xs text-zinc-500 ml-1">kWh</span>
                            </div>
                        </div>
                        <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden">
                            {["month", "custom"].map(t => (
                                <button key={t} onClick={() => { setTab(t); setHover(null); }}
                                    data-testid={`cust-tab-${t}`}
                                    className={`px-3 py-1.5 text-xs uppercase tracking-wider font-medium transition-colors duration-200 ${tab === t ? "bg-[#0B0D11] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
                                    {t === "month" ? "30 days" : "Custom"}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="h-72 relative">
                    {tab === "custom" && !rangeLoading && barData.length === 0 && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 text-center" data-testid="cust-range-empty">
                            <div>
                                <div className="text-sm font-medium text-zinc-500">No production recorded in this range</div>
                                <div className="text-xs text-zinc-400 mt-1">{rangeStart} → {rangeEnd}</div>
                            </div>
                        </div>
                    )}
                    {tab === "custom" && rangeLoading && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 text-xs text-zinc-500">Loading…</div>
                    )}
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={barData} onMouseMove={(s) => s?.activePayload?.[0] && setHover(s.activePayload[0].payload)} onMouseLeave={() => setHover(null)}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#71717A", fontFamily: 'IBM Plex Mono' }} />
                            <YAxis tick={{ fontSize: 11, fill: "#71717A", fontFamily: 'IBM Plex Mono' }} />
                            <Tooltip
                                contentStyle={{ borderRadius: 4, border: "1px solid #E4E4E7", fontSize: 12, fontFamily: "Outfit" }}
                                labelStyle={{ color: "#6B7280", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em" }}
                                formatter={(v) => [`${v.toFixed(1)} kWh`, "Generation"]}
                                cursor={{ fill: "rgba(249,115,22,0.06)" }}
                            />
                            <Bar dataKey="kWh" radius={[2, 2, 0, 0]}>
                                {barData.map((entry) => (
                                    <Cell key={entry.date} fill={entry.isToday ? "#16A34A" : "#0A0A0A"} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                {/* Live area chart of today */}
                <div className="mt-6">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-2">Today's Power Curve</div>
                    <div className="h-48">
                        {powerData.length === 0 ? (
                            <div className="h-full flex items-center justify-center text-xs text-zinc-500 border border-dashed border-zinc-200 rounded-sm">
                                No production yet today — the inverter will start generating at first light.
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={powerData}>
                                    <defs>
                                        <linearGradient id="cust-g1" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#16A34A" stopOpacity={0.5} />
                                            <stop offset="100%" stopColor="#16A34A" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                                    <XAxis dataKey="time" ticks={powerTicks} interval={0} tick={{ fontSize: 11, fill: "#71717A", fontFamily: 'IBM Plex Mono' }} />
                                    <YAxis domain={[0, 'auto']} tickFormatter={(v) => v.toFixed(1)} tick={{ fontSize: 11, fill: "#71717A", fontFamily: 'IBM Plex Mono' }} />
                                    <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid #E4E4E7", fontSize: 12 }} formatter={(v) => [`${v.toFixed(2)} kW`, "Power"]} />
                                    <Area type="monotone" dataKey="kW" stroke="#16A34A" strokeWidth={2} fill="url(#cust-g1)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>

            {/* Active alerts (customer view — read only) */}
            {activeAlerts.length > 0 && (
                <div className="bg-white border border-zinc-200 rounded-sm" data-testid="cust-alerts-panel">
                    <div className="p-4 border-b border-zinc-200">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Active Alerts ({activeAlerts.length})</div>
                        <div className="text-sm text-zinc-700 mt-0.5">Our team is already reviewing these. Reference codes below.</div>
                    </div>
                    <div className="divide-y divide-zinc-100">
                        {activeAlerts.map((a, i) => (
                            <div key={a.alertId} className="p-4" data-testid={`cust-alert-${i}`}>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-mono text-red-600 border border-red-500/30 bg-red-500/10 rounded-sm px-1.5 py-0.5">{a.protocolName}</span>
                                    <span className="text-sm font-medium text-zinc-900">{a.alertName}</span>
                                </div>
                                <div className="text-xs text-zinc-500">{a.description}</div>
                                <div className="text-[10px] font-mono text-zinc-400 mt-1">
                                    {new Date(a.alertStartTime * 1000).toLocaleString()}
                                </div>
                                <TechnicalGuide guide={a.technical_guide} compact testid={`cust-guide-${i}`} />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Tickets and Documents moved to their dedicated menu items (Support / Documents) */}
        </div>
    );
}

function KpiTile({ icon: Icon, label, value, unit, accent = "black", testid }) {
    const c = { orange: "text-[#0A0A0A]", green: "text-[#16A34A]", black: "text-zinc-900" }[accent];
    return (
        <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid={testid}>
            <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
                <Icon className="w-4 h-4 text-zinc-300" strokeWidth={2} />
            </div>
            <div className="flex items-baseline gap-1.5">
                <div className={`text-2xl font-medium font-mono ${c}`}>{value}</div>
                {unit && <div className="text-xs font-mono text-zinc-500">{unit}</div>}
            </div>
        </div>
    );
}

function MiniKpi({ label, value, unit, accent = "black" }) {
    const c = { orange: "text-[#0A0A0A]", green: "text-[#16A34A]", black: "text-zinc-900" }[accent];
    return (
        <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
            <div className="mt-1.5 flex items-baseline gap-1">
                <span className={`text-lg font-medium font-mono ${c}`}>{value}</span>
                {unit && <span className="text-[11px] font-mono text-zinc-500">{unit}</span>}
            </div>
        </div>
    );
}

function HeroCard({ icon: Icon, label, value, unit, sub, testid }) {
    return (
        <div
            className="relative overflow-hidden rounded-sm bg-[#0B0D11] border border-white/5 p-5 group hover:border-[#16A34A]/30 transition-colors duration-300"
            data-testid={testid}
        >
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#16A34A]/10 blur-2xl pointer-events-none" />
            <div className="relative flex items-start justify-between mb-4">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">{label}</div>
                <div className="w-9 h-9 rounded-sm bg-[#16A34A]/15 border border-[#16A34A]/30 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#16A34A]" strokeWidth={2.2} />
                </div>
            </div>
            <div className="relative flex items-baseline gap-2">
                <div className="text-3xl font-mono font-medium text-white leading-none tracking-tight">{value}</div>
                {unit && <div className="text-sm font-mono text-zinc-500">{unit}</div>}
            </div>
            {sub && <div className="relative text-[11px] text-zinc-500 mt-2 font-mono">{sub}</div>}
        </div>
    );
}
